import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Badge,
  Box,
  Menu,
  MenuItem
} from '@mui/material';
import { Message, AccountCircle, ExitToApp, ArrowBack, AcUnit, DeviceThermostat } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function AppHeader({ title, showBack = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleBack = () => {
    navigate(-1); // Browser-History zurück
  };

  return (
    <AppBar position="static" sx={{ backgroundColor: '#1976d2' }}>
      <Toolbar>
        {/* Zurück-Button */}
        {showBack && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
        )}
        
        {/* Logo */}
        <IconButton
          color="inherit"
          onClick={() => navigate('/dashboard')}
          sx={{ mr: 2 }}
        >
          <img 
            src="/logo192.png" 
            alt="Logo" 
            style={{ height: '40px', width: '40px' }}
          />
        </IconButton>
        
        {/* Datum + Uhrzeit kompakt neben Logo */}
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>
            {currentDateTime.toLocaleDateString('de-DE', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric' 
            })}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>
            {currentDateTime.toLocaleTimeString('de-DE', { 
              hour: '2-digit', 
              minute: '2-digit',
              second: '2-digit'
            })}
          </Typography>
        </Box>

        {/* Schnellzugriff-Buttons: KL und WP */}
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate('/protocol/klima-list')}
          sx={{
            backgroundColor: '#4fc3f7',
            color: 'white',
            minWidth: '45px',
            height: '45px',
            borderRadius: '50%',
            mr: 1,
            fontWeight: 'bold',
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: '#0288d1'
            }
          }}
        >
          KL
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate('/protocol/wp-protocols')}
          sx={{
            backgroundColor: '#ff9800',
            color: 'white',
            minWidth: '45px',
            height: '45px',
            borderRadius: '50%',
            mr: 2,
            fontWeight: 'bold',
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: '#f57c00'
            }
          }}
        >
          WP
        </Button>

        {/* Messages Badge */}
        <IconButton color="inherit" onClick={() => navigate('/messages')}>
          <Badge badgeContent={unreadCount} color="error">
            <Message />
          </Badge>
        </IconButton>

        {/* User Menu */}
        <IconButton
          color="inherit"
          onClick={handleUserMenuOpen}
          sx={{ ml: 1 }}
        >
          <AccountCircle />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleUserMenuClose}
        >
          <MenuItem disabled>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {user?.full_name || user?.username}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.role === 'admin' ? 'Administrator' : 'Monteur'}
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => { handleUserMenuClose(); logout(); }}>
            <ExitToApp sx={{ mr: 1 }} fontSize="small" />
            Abmelden
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
